/**
 * MIDI in and out: the format every other program can read, and almost none of notation.
 *
 * A MIDI file carries pitch, time and loudness. It does not carry spelling, articulation,
 * slurs, dynamics as words, lyrics, chord symbols, figured bass, cues, layout or anything
 * else a page shows, so a round trip through it loses all of those. It is the right format
 * for handing a performance to a sequencer and the wrong one for handing a score to an
 * engraver; MusicXML is the other door, and it keeps what this one drops.
 */

import { readMidi } from "./read";
import { transcribe } from "./transcribe";
import type { MidiImport, MidiImportOptions } from "./transcribe";

export { toMidi, type MidiWriteOptions } from "./write";
export {
  readMidi,
  type MidiFile,
  type MidiKeyMark,
  type MidiNote,
  type MidiRead,
  type MidiTempoMark,
  type MidiTimeMark,
  type MidiTrack,
} from "./read";
export { transcribe, type MidiImport, type MidiImportOptions } from "./transcribe";
export { NOTHING_HELD, pressed, type HeldKeys, type KeyPress, type PlayedInput } from "./keyboard";

/**
 * Read a MIDI file and write it down as a score.
 *
 * The two failures are told apart: a file that is not a MIDI file at all comes back with
 * the reader's reason, and a file that is one but holds nothing writable comes back with
 * the transcriber's. Anything guessed along the way is listed in `warnings`.
 */
export function fromMidi(data: Uint8Array, options: MidiImportOptions = {}): MidiImport {
  const read = readMidi(data);
  if (!read.ok) return { ok: false, reason: read.reason };
  return transcribe(read.file, options);
}
